import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { AnchorBar, AnchorBarList, AnchorBarListItem, tokensRaw } from '@vp/swan'
import { useStyles } from '@vp/ubik-context'
import { MARKETING_COLORS } from '@vp/marketing-colors'

import { useAppContext } from '../state/AppContext'
import { pageCategoryPageV3 } from '../types/Contentful/ContentfulTypes'
import {
  CategoryPageSection,
  AnchorLinksType,
  AnchorLinkItemsType
} from '../types/TemplateConfiguration'
import { getAnchorLinksPropsBasedOnSectionAvailability } from '../utils/anchorLinkUtils/anchorLinksUtil'
import { stylesheet } from '../styles/AnchorBar.scss'
import { trackAnchorBarClickEvent } from '../utils/tracking/tracking'
import { getHash } from '../utils/location/location'

interface AnchorBarWrapperProps {
  pageCategoryPageV3: pageCategoryPageV3
  sections: CategoryPageSection[]
}

const AnchorBarWrapper: React.FC<AnchorBarWrapperProps> = (props: AnchorBarWrapperProps) => {
  useStyles(stylesheet)
  const [activeAnchor, setActiveAnchor] = React.useState()
  const { merchandisingCategoryId } = useAppContext()
  const anchorListNode = useRef<HTMLDivElement>(null)
  const { sections } = props

  const anchorLinkSection = getAnchorLinksPropsBasedOnSectionAvailability(
    sections
  ) as AnchorLinksType

  const anchorLinkItems = anchorLinkSection?.items

  if (!anchorLinkItems) {
    return <></>
  }

  useEffect(() => {
    const element = anchorListNode.current

    const trackAnchorLinkClick = (event: MouseEvent) => {
      const { innerText } = event.target as HTMLElement
      trackAnchorBarClickEvent({
        categoryId: merchandisingCategoryId as string,
        label: innerText
      })
    }
    element?.addEventListener('click', trackAnchorLinkClick)

    return () => {
      element?.removeEventListener('click', trackAnchorLinkClick)
    }
  }, [])

  useLayoutEffect(() => {
    const sectionId = getHash()
    const sectionById = document.getElementById(sectionId)

    let timeout: NodeJS.Timeout

    const onLoad = (event: Event) => {
      event.preventDefault()
      if (sectionById) {
        timeout = setTimeout(() => {
          const { top } = sectionById.getBoundingClientRect()
          const anchorBarHeight = document.querySelector('.swan-anchor-bar-list')?.clientHeight || 0

          const SCROLL_MARGIN = parseInt(tokensRaw.SwanBaseSpace200) || 0
          const scrollValue = window.scrollY + top - anchorBarHeight - SCROLL_MARGIN
          window.scrollTo({ top: scrollValue, behavior: 'smooth' })
          setActiveAnchor(sectionId)
        }, 1500)
      }
    }

    document.addEventListener('load', onLoad, { once: true, capture: true })

    return () => {
      clearTimeout(timeout)
      document.removeEventListener('load', onLoad)
    }
  }, [])

  const backgroundColor = MARKETING_COLORS[anchorLinkSection.backgroundColor.name]
  return (
    <AnchorBar
      className="anchor-bar-spacing anchor-bar-negative-margin"
      sticky
      style={{ backgroundColor: backgroundColor?.code }}
      onActiveAnchorChanged={setActiveAnchor}
      darkMode={backgroundColor?.mode.dark}
    >
      <AnchorBarList ref={anchorListNode}>
        {anchorLinkItems.map((item: AnchorLinkItemsType) => {
          const sectionId = item.categorySectionSectionId
          return (
            <AnchorBarListItem key={sectionId}>
              <a
                href={`#${sectionId}`}
                aria-current={activeAnchor === sectionId ? 'true' : undefined}
              >
                {item.title?.value}
              </a>
            </AnchorBarListItem>
          )
        })}
      </AnchorBarList>
    </AnchorBar>
  )
}

export default AnchorBarWrapper
